import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http, inr, requestErrorMessage, mailtoLink } from "@/lib/erp";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { SDOC, TARGET_ROUTE } from "@/lib/erpdocs";
import DocumentPrint from "@/components/DocumentPrint";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, ArrowRightLeft, Pencil, Ban, CheckCircle2, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

export default function SalesDocDetail({ dtype }) {
  const requestWhatsAppSend = useWhatsAppSender();
  const cfg = SDOC[dtype];
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try { const r = await http.get(`/sales-docs/${cfg.api}/${id}`); setD(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, `load ${cfg.label.toLowerCase()}`)); }
  };
  useEffect(() => { setD(null); setLoadError(""); load(); }, [id, dtype]);
  if (!d) return <div className="p-8 space-y-3">
    <div className={loadError ? "text-rose-600" : "text-muted-foreground"}>{loadError || "Loading…"}</div>
    {loadError && <Button size="sm" variant="outline" onClick={load}>Retry</Button>}
  </div>;
  const c = d.company || {};

  const convert = async (target) => {
    try {
      const { data } = await http.post(`/sales-docs/${cfg.api}/${id}/convert`, { target });
      toast.success(`Converted · ${data.no}`); navigate(`${TARGET_ROUTE[target]}/${data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, `convert ${cfg.label.toLowerCase()}`)); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel?")) return;
    try { await http.post(`/sales-docs/${cfg.api}/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, `cancel ${cfg.label.toLowerCase()}`)); }
  };

  const shareMsg = `Hello ${d.customer_name},\n\nPlease find details of ${cfg.label} ${d.doc_no} dated ${d.doc_date}.\nTotal: ${inr(d.grand_total)}\n\nThank you,\n${(d.company || {}).name || ""}`;
  const shareWhatsApp = () => requestWhatsAppSend({ phone: d.customer_phone, message: shareMsg, title: `${cfg.label} ${d.doc_no}` });
  const shareEmail = () => { if (!d.customer_email) return toast.error("No email saved for this customer"); window.location.href = mailtoLink(d.customer_email, `${cfg.label} ${d.doc_no}`, shareMsg); };

  const chainNodes = [...(d.source_documents || []).map((s) => ({ label: s.label, no: s.no, done: true })),
    { label: cfg.label, no: d.doc_no, active: true },
    ...(d.converted_to || []).map((s) => ({ label: s.type.replace("_", " "), no: s.no, done: true }))];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="no-print space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => navigate(cfg.base)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
          <Button size="sm" variant="print" onClick={() => window.print()} data-testid="sdoc-print-button"><Printer className="h-4 w-4 mr-1" /> Print / PDF</Button>
        </div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold font-mono">{d.doc_no}</h1>
            <p className="text-sm text-muted-foreground mt-1">{d.customer_name} · {d.doc_date}</p>
          </div>
          <div className="text-right"><div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div><div className="font-heading text-2xl font-bold tabular">{inr(d.grand_total)}</div></div>
        </div>

        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Document Chain</div>
          <div className="flex items-center gap-2 flex-wrap">
            {chainNodes.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border capitalize ${s.active ? "bg-primary text-primary-foreground border-primary" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                  {s.done && <CheckCircle2 className="h-3.5 w-3.5" />}{s.label} <span className="font-mono text-xs opacity-80">{s.no}</span>
                </div>
                {i < chainNodes.length - 1 && <div className="h-px w-5 bg-slate-300" />}
              </React.Fragment>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex flex-wrap gap-2">
            {cfg.convert.map(([t, label]) => (
              <Button key={t} size="sm" onClick={() => convert(t)} data-testid={`convert-${t}`}><ArrowRightLeft className="h-4 w-4 mr-1" /> {label}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => navigate(`${cfg.base}/${id}/edit`)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
            <Button size="sm" variant="outline" onClick={shareWhatsApp} data-testid="whatsapp-button"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
            <Button size="sm" variant="outline" onClick={shareEmail} data-testid="email-button"><Mail className="h-4 w-4 mr-1" /> Email</Button>
            <Button size="sm" variant="outline" className="text-rose-600" onClick={cancel}><Ban className="h-4 w-4 mr-1" /> Cancel</Button>
          </div>
        </Card>

        <div className="text-xs uppercase tracking-wider text-muted-foreground">Preview (A4)</div>
        <div className="border border-slate-200 rounded-lg overflow-auto bg-slate-100 p-4">
          <div className="shadow-lg mx-auto" style={{ width: "fit-content" }}><DocumentPrint doc={d} company={c} title={cfg.label} type={dtype} printArea={false} /></div>
        </div>
      </div>
      <div className="hidden print:block"><DocumentPrint doc={d} company={c} title={cfg.label} type={dtype} printArea /></div>
    </div>
  );
}
